import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFocusVisibleWithin} from '@github-ui/focus-visible'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {lazy, Suspense, useMemo} from 'react'

import {ErrorFallback} from './ErrorFallback'
import styles from './HybridMarkdownEditor.module.css'
import {SuspenseFallback} from './SuspenseFallback'
import {HybridEditorTelemetryProvider} from './telemetry'
import {CustomToolbarButton} from './toolbar/CustomToolbarButton'
import type {HybridMarkdownEditorProps} from './types'

const loadCodeMirrorEditor = async () => {
  const module = await import('./CodeMirrorEditor')
  return {default: module.CodeMirrorEditor}
}

const CodeMirrorEditor = lazy(loadCodeMirrorEditor)

export async function preloadHybridMarkdownEditor() {
  await loadCodeMirrorEditor()
}

// IMPORTANT: Keep this component as minimal as possible, with no logic that might possibly throw an error, since any
// errors thrown in this component wouldn't be caught by the error boundary
function HybridMarkdownEditorComponent({
  onChangeViewMode,
  value,
  placeholder,
  ref,
  isGenerating,
  ...restProps
}: HybridMarkdownEditorProps) {
  const focusVisibleRef = useFocusVisibleWithin()
  return (
    <div className={styles.container} ref={focusVisibleRef}>
      <ErrorBoundary
        boundaryName="hybrid-markdown-editor"
        fallback={<ErrorFallback onChangeViewMode={onChangeViewMode} />}
      >
        <HybridEditorTelemetryProvider
          options={useMemo(() => ({featureFlagEnabled: isFeatureEnabled('markdown_experience_hybrid_editor')}), [])}
        >
          <Suspense fallback={<SuspenseFallback value={value} placeholder={placeholder} />}>
            <CodeMirrorEditor
              ref={ref}
              onChangeViewMode={onChangeViewMode}
              value={value}
              placeholder={placeholder}
              isGenerating={isGenerating}
              {...restProps}
            />
          </Suspense>
        </HybridEditorTelemetryProvider>
      </ErrorBoundary>
    </div>
  )
}

export const HybridMarkdownEditor = Object.assign(HybridMarkdownEditorComponent, {
  ToolbarButton: CustomToolbarButton,
})
